import { useAuth0 } from '@auth0/auth0-react';
import { useUserState } from '@common/context/userContext';
import ThemeToggle from '@ds/components/actions/ThemeToggle';
import Flex from '@ds/components/layout/Flex';
import { BP_DESKTOP } from '@ds/foundations/tokens';
import useUserDetails from '@effects/useUserdetails';
import { canViewTemplate, IsValidUser } from '@utils/question-editable';
import React from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router';
import { graphql, useQuery } from 'relay-hooks';

import Brand from './Brand';
import {
    Desktop as ConsignmentEditHeaderDesktop,
    Mobile as ConsignmentEditHeaderMobile,
} from './ConsignmentEditHeader';
import { useHeaderActions } from './HeaderActionsContext';
import MobileNav from './MobileNav';
import styles from './NavigationBar.module.css';
import NavItems from './NavItems';
import UserMenu from './UserMenu';
import useSaveOnExit from './useSaveOnExit';

export const getConsignmentNumberFromPath = (pathname: string): string | undefined =>
    pathname.split('/').find((segment) => /^ct?-/i.test(segment));

const query = graphql`
    query NavigationBarQuery($number: String!, $envdAccountId: String!) {
        consignment(id: $number, envdAccountId: $envdAccountId) {
            id
            number
            updatedAt
            status
        }
    }
`;

interface Props {
    menuOpen: boolean;
    closeAction: () => void;
}

const NavigationBar: React.FC<Props> = ({ closeAction, menuOpen }) => {
    const location = useLocation();
    const isTabletOrMobile = useMediaQuery({ maxWidth: BP_DESKTOP - 1 });

    const { isAuthenticated } = useAuth0();
    const { isLoading } = useUserDetails();
    const [{ user }] = useUserState();
    const isLoggedIn = Boolean(isAuthenticated && !isLoading);
    const isValidUser = IsValidUser(user);
    const canView = canViewTemplate(user);
    const envdAccountId = user?.accountDetails?.id;
    const { actions } = useHeaderActions();

    // The consignment number sits at different depths (/consignments/C-123/movement,
    // /consignments/edit/C-123/movement, /consignments/summary/C-123), so match the
    // segment by its C-/CT- prefix rather than by position.
    const number = getConsignmentNumberFromPath(location.pathname);
    const { data } = useQuery<any>(query, { number: number!, envdAccountId }, { skip: !number });
    const consignment = data?.consignment;

    const isInConsignmentEditRoute = location.pathname.startsWith('/consignments');
    const showConsignmentHeader = isInConsignmentEditRoute && consignment;

    const saveOnExit = useSaveOnExit();

    const navItemsProps = {
        isLoggedIn,
        canView,
        isValidUser,
        currentPath: location.pathname,
        onNavigate: saveOnExit,
    };

    if (isTabletOrMobile) {
        return (
            <Flex
                direction="row"
                align="center"
                grow="row"
                gap={8}
            >
                {showConsignmentHeader ? <ConsignmentEditHeaderMobile consignment={consignment} /> : <Brand />}
                <span className={styles.flexGrow} />
                {actions && <span className={styles.actions}>{actions}</span>}
                <ThemeToggle />
                <MobileNav
                    menuOpen={menuOpen}
                    onClose={closeAction}
                    isLoggedIn={isLoggedIn}
                    hasUser={!!user?.accountDetails}
                >
                    <NavItems {...navItemsProps} />
                </MobileNav>
            </Flex>
        );
    }

    return (
        <Flex
            direction="row"
            gap={8}
            grow="row"
            align="center"
            justify="center"
        >
            {showConsignmentHeader && (
                <ConsignmentEditHeaderDesktop
                    consignment={consignment}
                    onBack={() => saveOnExit('/consignments')}
                    currentPath={location.pathname}
                />
            )}
            <Flex
                direction="row"
                gap={6}
                align="center"
                justify="between"
                grow="row"
            >
                {!showConsignmentHeader && <NavItems {...navItemsProps} />}
                {actions && <span className={styles.actions}>{actions}</span>}
                <span className={styles.flexGrow} />
                <ThemeToggle />
                {isLoggedIn && <UserMenu />}
            </Flex>
        </Flex>
    );
};

export default NavigationBar;
